export class QueuedTaskAborted extends Error {
    constructor() {
        super("Queued task aborted before start")
        this.name = "QueuedTaskAborted"
    }
}

type QueuedTask = {
    priority: number
    signal: AbortSignal
    run: () => Promise<unknown>
    resolve: (value: unknown) => void
    reject: (err: unknown) => void
}

/** Runs up to `limit` tasks at once, starting the highest priority of whatever is waiting. */
export const createPriorityQueue = (limit: number) => {
    const waiting: QueuedTask[] = []
    let active = 0

    const pump = () => {
        while (active < limit && waiting.length > 0) {
            let best = 0
            for (let i = 1; i < waiting.length; i++) {
                if (waiting[i].priority > waiting[best].priority) best = i
            }
            const task = waiting.splice(best, 1)[0]
            if (task.signal.aborted) {
                task.reject(new QueuedTaskAborted())
                continue
            }
            active++
            task.run()
                .then(task.resolve, task.reject)
                .finally(() => {
                    active--
                    pump()
                })
        }
    }

    return <T>(priority: number, signal: AbortSignal, run: () => Promise<T>) =>
        new Promise<T>((resolve, reject) => {
            waiting.push({
                priority,
                signal,
                run: run as () => Promise<unknown>,
                resolve: resolve as (value: unknown) => void,
                reject,
            })
            pump()
        })
}
